import eventMixin from '../lib/eventMixin';
import KeenSlider from 'keen-slider';
import Player from '@vimeo/player';
import ThemeMediaLightboxImage from './themeMediaLightboxImage';
import ThemeMediaLightboxVideo from './themeMediaLightboxVideo';
import Logger from "../utilities/Logger";

const classes = {
	htmlNoScroll: 'global-html--blacked-out',
	open: 'theme-media-lightbox--open'
}

const arrowActiveClass = 'theme-media-lightbox__arrow--active';
const closeModalDataAttribute = 'data-theme-media-lightbox-close';
const paginationContainerActiveClass = 'theme-media-lightbox__pagination--active';
const paginationDotActiveClass = 'theme-media-lightbox__pagination-dot--active';
const paginationDotDataAttribute = 'data-theme-media-lightbox-pagination-dot';
const slidesDataAttribute = 'data-theme-media-lightbox-slide';

export default class ThemeMediaLightbox {

	static events = {
		close: 'theme-media-lightbox.close',
		open: 'theme-media-lightbox.open',
	}

	constructor(dom) {

		var self = this;

		this.initialized = false;

		this.dom = dom;
		this.arrowNext = this.dom.querySelector('[data-theme-media-lightbox-show-next]');
		this.arrowPrev = this.dom.querySelector('[data-theme-media-lightbox-show-prev]');
		this.slidesContainer = this.dom.querySelector('[data-theme-media-lightbox-slides]');
		this.slideContentControllers = [];
		this.slideshow = null;
		this.closeAction = dom.querySelector('[data-theme-media-lightbox-close-action]');
		this.paginationContainer = dom.querySelector('[data-theme-media-lightbox-pagination]');

		this.listenForBackgroundClicksBindingFn = this.listenForBackgroundClicks.bind(this);
	}

	init() {
		const NAME = 'ThemeMediaLightbox';
		if (this.initialized) {
			Logger.log(`${NAME} > init`);
		}

		const self = this;

		// Listen for open events
		document.addEventListener(ThemeMediaLightbox.events.open, (event) => {
			const { mediaItems } = event.detail;
			self.open(mediaItems);
		});

		// Listen for close events
		document.addEventListener(ThemeMediaLightbox.events.close, (event) => {
			self.close();
		});

		// Request close on click of close action
		this.closeAction.addEventListener('click', (e) => {
			self.close();
		});

		this.initialized = true;
	}

	// Adjust various classes according to the active slide
	adjustClasses() {

		if (!this.slideshow) {
			return;
		}

		const activeSlide = this.slideshow.track.details.rel;

		const dots = this.paginationContainer ? this.paginationContainer.querySelectorAll(`[${paginationDotDataAttribute}]`) : [];

		if (dots[activeSlide]) {
			dots.forEach((dot) => {
				dot.classList.remove(paginationDotActiveClass);
				dot.setAttribute('aria-selected', 'false');
			});
			dots[activeSlide].classList.add(paginationDotActiveClass);
			dots[activeSlide].setAttribute('aria-selected', 'true');
		}
	}

	// Build the next / prev arrows
	buildArrows(mediaItems) {

		const self = this;

		if (mediaItems.length <= 1) {

			// Uninitialize arrows
			this.arrowNext?.classList.remove(arrowActiveClass);
			this.arrowNext?.removeEventListener('click', this.showNextSlide);
			this.arrowPrev?.classList.remove(arrowActiveClass);
			this.arrowPrev?.removeEventListener('click', this.showPrevSlide);

			return;
		}

		Logger.info('Building arrows...');

		// Initialize arrows
		this.arrowNext?.classList.add(arrowActiveClass);
		this.arrowNext?.addEventListener('click', this.showNextSlide.bind(this));

		this.arrowPrev?.classList.add(arrowActiveClass);
		this.arrowPrev?.addEventListener('click', this.showPrevSlide.bind(this));

	}

	// Create all slides to fill the lightbox
	buildMediaItems(mediaItems) {

		const self = this;

		this.slideContentControllers = [];
		this.slidesContainer.innerHTML = '';

		if (!Array.isArray(mediaItems)) {
			console.error('Provided media items are not an array.');
			return;
		}

		mediaItems.forEach((item) => {

			if (item.type === undefined) {
				console.error('Item does not expose a type.');
				return;
			}

			let htmlDom;
			switch (item.type) {
				case 'image':
					htmlDom = self.buildImageMediaItem(item);
					break;
				case 'vimeo':
					htmlDom = self.buildVimeoMediaItem(item);
					break;
				default:
					break;
			}

			if (htmlDom) {
				self.slidesContainer.appendChild(htmlDom);
			}
		});
	}

	// Build the shell of a slide (empty)
	buildSlide() {
		const slide = document.createElement('div');
		slide.classList.add('theme-media-lightbox__slide');
		slide.setAttribute(slidesDataAttribute, true);
		return slide;
	}

	// Build an slide
	buildImageMediaItem(item) {

		const dom = ThemeMediaLightboxImage.generateHtml(item.src);

		const slideContentController = new ThemeMediaLightboxImage(dom);
		this.slideContentControllers.push(slideContentController);

		const slide = this.buildSlide();
		slide.appendChild(dom);

		return slide;
	}

	// Build the pagination dots at the bottom of the modal
	buildPaginationDots(mediaItems) {

		Logger.info('Building pagination dots...');

		if (!this.paginationContainer) {
			return;
		}

		const self = this;

		this.paginationContainer.innerHTML = '';

		if (mediaItems.length <= 1) {
			this.paginationContainer.classList.remove(paginationContainerActiveClass);
			return;
		}

		this.paginationContainer.classList.add(paginationContainerActiveClass);

		this.slideContentControllers?.forEach((item, i) => {
			const dom = document.createElement('button');
			dom.classList.add(`theme-media-lightbox__pagination-dot`);
			if (i == 0) {
				dom.classList.add(paginationDotActiveClass);
			}
			dom.setAttribute(paginationDotDataAttribute, i);
			dom.setAttribute('aria-label', `View slide ${i + 1}`);
			dom.setAttribute('role', 'tab');

			dom.addEventListener('click', (e) => {
				const idx = dom.getAttribute(paginationDotDataAttribute);
				Logger.info(`Pagination dot was clicked, adjusting slideshow to slide ${idx}`);
				self.slideshow?.moveToIdx(idx * 1);
			});

			self.paginationContainer.appendChild(dom);
		});

	}

	// Build a Vimeo slide
	buildVimeoMediaItem(item) {

		const dom = ThemeMediaLightboxVideo.generateHtml(item.id);

		const slideContentController = new ThemeMediaLightboxVideo(dom);
		this.slideContentControllers.push(slideContentController);

		const slide = this.buildSlide();
		slide.appendChild(dom);

		return slide;
	}

	// Close the modal
	close() {
		Logger.info('Closing modal...');
		document.querySelector('.global-html')?.classList.remove(classes.htmlNoScroll);
		this.dom.classList.remove(classes.open);
		this.slideshow?.destroy();
		this.slideshow = null;
		this.destroySlides();
		this.slidesContainer.innerHTML = '';
		this.dom.removeEventListener('click', this.listenForBackgroundClicksBindingFn);
		this.trigger('closed');
	}

	// Loop thru each slide and instruct it to self destruct
	destroySlides() {
		this.slideContentControllers.forEach((controller) => {
			controller.destroy?.();
		});
		this.slideContentControllers = [];
	}

	// Loop thru each slide and instruct it to initialize
	initSlides() {

		const self = this;

		this.slideContentControllers.forEach((controller) => {
			controller.init?.();
		});

		// Set up the slideshow
		if (!this.slideshow) {

			Logger.info('Initializing slideshow...');

			// Initialize Keen
			this.slideshow = new KeenSlider(this.slidesContainer, {
				selector: this.dom.querySelectorAll(`[${slidesDataAttribute}]`),
				loop: true,
				created: () => {
					Logger.info('ThemeMediaLightbox Slideshow: created...');
					self.trigger('slideshow-created');

					// We have to wait a bit because the this.slideshow var is not
					// immediately available since we are in the created event still
					setTimeout(() => {
						self.adjustClasses();
					}, 100);
				},
				destroyed: () => {
					Logger.info('ThemeMediaLightbox Slideshow: destroyed...');
					self.trigger('slideshow-destroyed');
				},
				slideChanged: () => {
					Logger.info('ThemeMediaLightbox Slideshow: slideChanged...');
					self.adjustClasses();
				},
			});

		}
	}

	// Listen for background clicks and close modal if found
	listenForBackgroundClicks(e) {
		if (e.target.getAttribute(closeModalDataAttribute)) {
			Logger.info('Requested to close...');
			this.close();
		}
	}

	// Open the modal
	open(mediaItems) {

		const self = this;

		Logger.info('Opening media items in modal...', mediaItems);

		this.buildMediaItems(mediaItems);
		this.buildArrows(mediaItems);
		this.buildPaginationDots(mediaItems);
		this.initSlides();
		document.querySelector('.global-html')?.classList.add(classes.htmlNoScroll);
		this.dom.classList.add(classes.open);
		this.dom.addEventListener('click', this.listenForBackgroundClicksBindingFn);
		this.trigger('opened');
	}

	pauseAllVideos() {
		this.slideContentControllers.forEach((v) => {
			if (v.pause) {
				v.pause();
			}
		});
	}

	showNextSlide() {
		Logger.info('Showing next slide...');
		this.pauseAllVideos();
		this.slideshow?.next();
	}

	showPrevSlide() {
		Logger.info('Showing prev slide...');
		this.pauseAllVideos();
		this.slideshow?.prev();
	}
}

// Mixins
Object.assign(ThemeMediaLightbox.prototype, eventMixin);
